import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  ModelError_Item,
  PrevailingWageFringeOvertimeTreatment,
  PrevailingWageOvertimeScope,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  OvertimeRule,
  OvertimeRuleFormData,
  OvertimeRuleFringeTreatment,
  OvertimeRuleScope,
} from '~hooks/useOvertimeRule/models';
import useOvertimeRule from '~hooks/useOvertimeRule/useOvertimeRule';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getPageNumber } from '~utils/pagination';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import OvertimeRuleForm, { OvertimeRuleFormHandler } from './OvertimeRuleForm';
import { OvertimeRuleDto, OvertimeRuleDtoAsyncError } from './overtimeRuleSchema';

type OvertimeRuleDialogState = {
  isOpen: boolean;
  isSubmitting: boolean;
  overtimeRule?: OvertimeRule;
};

function getScopeDisplayValue(scope: OvertimeRuleScope): string {
  const scopeLabels: Record<OvertimeRuleScope, string> = {
    [PrevailingWageOvertimeScope.PW_HOURS_ONLY]: t(
      'administration.overtime_rule.scope_options.pw_hours_only',
    ),
    [PrevailingWageOvertimeScope.ALL_EMPLOYER_HOURS]: t(
      'administration.overtime_rule.scope_options.all_employer_hours',
    ),
  };

  return scopeLabels[scope] ?? scope;
}

function getFringeTreatmentDisplayValue(
  fringeTreatment: OvertimeRuleFringeTreatment,
): string {
  const fringeTreatmentLabels: Record<OvertimeRuleFringeTreatment, string> = {
    [PrevailingWageFringeOvertimeTreatment.STRAIGHT_TIME]: t(
      'administration.overtime_rule.fringe_treatment_options.straight_time',
    ),
    [PrevailingWageFringeOvertimeTreatment.PREMIUM]: t(
      'administration.overtime_rule.fringe_treatment_options.premium',
    ),
    [PrevailingWageFringeOvertimeTreatment.REDUCED]: t(
      'administration.overtime_rule.fringe_treatment_options.reduced',
    ),
  };

  return fringeTreatmentLabels[fringeTreatment] ?? fringeTreatment;
}

function dtoToFormData(dto: OvertimeRuleDto) {
  // The Yup schema validates scope and fringe_treatment against enum values via .oneOf(),
  // so these casts are safe after schema validation passes
  const result: OvertimeRuleFormData = {
    name: dto.name,
    scope: dto.scope as OvertimeRuleScope,
    dailyThresholdHours: dto.daily_threshold_hours ?? null,
    weeklyThresholdHours: dto.weekly_threshold_hours,
    weeklyThresholdDays: dto.weekly_threshold_days ?? null,
    multiplier: dto.multiplier,
    doubleTimeThresholdHours: dto.double_time_threshold_hours ?? null,
    doubleTimeMultiplier: dto.double_time_multiplier ?? null,
    fringeTreatment: dto.fringe_treatment as OvertimeRuleFringeTreatment,
  };

  return result;
}

export const OvertimeRuleManagement = observer(() => {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { createOvertimeRule, deleteOvertimeRule, getOvertimeRules, updateOvertimeRule } =
    useOvertimeRule();

  const [overtimeRules, setOvertimeRules] = useState<OvertimeRule[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [isLoading, setIsLoading] = useState(false);

  const overtimeRuleFormRef = useRef<OvertimeRuleFormHandler>(null);
  const [formDialogState, setFormDialogState] = useState<OvertimeRuleDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [formDialogAsyncErrors, setFormDialogAsyncErrors] = useState<
    OvertimeRuleDtoAsyncError[]
  >([]);
  const [deleteDialogState, setDeleteDialogState] = useState<OvertimeRuleDialogState>({
    isOpen: false,
    isSubmitting: false,
  });

  const fetchOvertimeRules = useCallback(
    async (link?: PaginationLink, cursor?: string) => {
      if (!companyId) {
        return;
      }

      setIsLoading(true);

      try {
        const result = await getOvertimeRules(companyId, link, cursor);
        setOvertimeRules(result.data);
        setPagination((previousPagination) => ({
          ...result.pagination,
          page: getPageNumber(previousPagination?.page ?? 1, link),
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, getOvertimeRules],
  );

  const refetchFirstPage = useCallback(() => {
    void fetchOvertimeRules().catch(() => {
      // Error toast is handled in the hook.
    });
  }, [fetchOvertimeRules]);

  useEffect(() => {
    refetchFirstPage();
  }, [refetchFirstPage]);

  const handleCreateClick = useCallback(() => {
    setFormDialogState({ isOpen: true, isSubmitting: false });
  }, []);

  const handleEditClick = useCallback((overtimeRule: OvertimeRule) => {
    setFormDialogState({
      isOpen: true,
      isSubmitting: false,
      overtimeRule,
    });
  }, []);

  const handleDeleteClick = useCallback((overtimeRule: OvertimeRule) => {
    setDeleteDialogState({
      isOpen: true,
      isSubmitting: false,
      overtimeRule,
    });
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await overtimeRuleFormRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    const overtimeRuleId = formDialogState.overtimeRule?.id;
    const data = dtoToFormData(formData);

    try {
      setFormDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSubmitting: true }));

      if (overtimeRuleId) {
        const updated = await updateOvertimeRule(companyId, overtimeRuleId, data);

        if (updated) {
          setOvertimeRules((prev) =>
            prev.map((rule) => (rule.id === overtimeRuleId ? updated : rule)),
          );
          toasterStore.push(
            alert(
              t('administration.overtime_rule.updated', {
                name: updated.name,
              }),
              AlertTypes.success,
            ),
          );
        }
      } else {
        const created = await createOvertimeRule(companyId, data);

        if (created) {
          refetchFirstPage();
          toasterStore.push(
            alert(
              t('administration.overtime_rule.created', {
                name: created.name,
              }),
              AlertTypes.success,
            ),
          );
        }
      }

      setFormDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setFormDialogState((state) => ({ ...state, isSubmitting: false }));

      if (axios.isAxiosError(error)) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: OvertimeRuleDtoAsyncError[] = [];
        const fieldMap: Record<string, keyof OvertimeRuleDto> = {
          daily_threshold_hours: 'daily_threshold_hours',
          double_time_multiplier: 'double_time_multiplier',
          double_time_threshold_hours: 'double_time_threshold_hours',
          fringe_treatment: 'fringe_treatment',
          multiplier: 'multiplier',
          name: 'name',
          scope: 'scope',
          weekly_threshold_days: 'weekly_threshold_days',
          weekly_threshold_hours: 'weekly_threshold_hours',
        };

        if (error.response?.status === 422) {
          errorsArray.forEach((item) => {
            const { field, message } = item;
            const formField = fieldMap[field];

            if (formField) {
              asyncErrors.push({ field: formField, message });
            }
          });
        }

        if (asyncErrors.length) {
          setFormDialogAsyncErrors(asyncErrors);
        }
      }
    }
  }, [
    companyId,
    createOvertimeRule,
    formDialogState.overtimeRule?.id,
    refetchFirstPage,
    updateOvertimeRule,
  ]);

  const handleConfirmDelete = useCallback(async () => {
    const overtimeRuleId = deleteDialogState.overtimeRule?.id;

    if (!overtimeRuleId) {
      return;
    }

    try {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: true }));
      await deleteOvertimeRule(companyId, overtimeRuleId);

      refetchFirstPage();
      toasterStore.push(
        alert(t('administration.overtime_rule.deleted'), AlertTypes.success),
      );
      setDeleteDialogState({ isOpen: false, isSubmitting: false });
    } catch {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: false }));
    }
  }, [
    companyId,
    deleteDialogState.overtimeRule?.id,
    deleteOvertimeRule,
    refetchFirstPage,
  ]);

  const columns = useMemo<Array<DataTableColumn<OvertimeRule>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (overtimeRule) => overtimeRule.name,
      },
      {
        field: 'scope',
        headerName: t('form_fields.scope'),
        valueGetter: (overtimeRule) => getScopeDisplayValue(overtimeRule.scope),
      },
      {
        field: 'weeklyThresholdHours',
        headerName: t('form_fields.weekly_threshold_hours'),
        valueGetter: (overtimeRule) => overtimeRule.weeklyThresholdHours,
      },
      {
        field: 'dailyThresholdHours',
        headerName: t('form_fields.daily_threshold_hours'),
        valueGetter: (overtimeRule) => overtimeRule.dailyThresholdHours ?? '-',
      },
      {
        field: 'multiplier',
        headerName: t('form_fields.multiplier'),
        valueGetter: (overtimeRule) => `${overtimeRule.multiplier}x`,
      },
      {
        field: 'fringeTreatment',
        headerName: t('form_fields.fringe_treatment'),
        valueGetter: (overtimeRule) =>
          getFringeTreatmentDisplayValue(overtimeRule.fringeTreatment),
      },
      actionsColumn((overtimeRule) => (
        <Box sx={{ display: 'flex' }}>
          <IconButton size="small" onClick={() => handleEditClick(overtimeRule)}>
            <Edit />
          </IconButton>

          <IconButton size="small" onClick={() => handleDeleteClick(overtimeRule)}>
            <Delete />
          </IconButton>
        </Box>
      )),
    ],
    [handleDeleteClick, handleEditClick],
  );

  useEffect(() => {
    if (!formDialogState.isOpen) {
      setFormDialogAsyncErrors([]);
    }
  }, [formDialogState.isOpen]);

  return (
    <>
      <SettingsPageHeader
        title={t('settings.nav_items.ot_rules')}
        description={t('settings.page_descriptions.ot_rules')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          onClick={handleCreateClick}
          startIcon={<Add />}
        >
          {t('administration.overtime_rule.create')}
        </SmallButton>
      </Box>
      <Box data-test-id="overtime-rule-data-grid">
        <DataTable
          id="overtime-rule-data-grid"
          columns={columns}
          rows={overtimeRules}
          loading={isLoading}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isLoading}
              onNavigate={(link) => {
                void fetchOvertimeRules(
                  link,
                  link ? pagination?.[link] : undefined,
                ).catch(() => {
                  // Error toast is handled in the hook.
                });
              }}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.overtimeRule
            ? t('administration.overtime_rule.update')
            : t('administration.overtime_rule.create')}
        </DialogHeader>

        <DialogContent>
          <OvertimeRuleForm
            ref={overtimeRuleFormRef}
            asyncErrors={formDialogAsyncErrors}
            overtimeRule={formDialogState.overtimeRule}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(formDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.overtime_rule.invalid_data_error')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={formDialogState.isSubmitting}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={formDialogState.isSubmitting}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.overtimeRule ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isBusy={deleteDialogState.isSubmitting}
        isOpen={deleteDialogState.isOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title={t('administration.overtime_rule.delete')}
      >
        <Typography>{t('administration.overtime_rule.delete_description')}</Typography>
      </ConfirmationDialog>
    </>
  );
});
